import { Check, ChevronsUpDown } from "lucide-react";
import { ReactNode, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  keywords?: string[];
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  listHeader?: ReactNode;
  renderOption?: (option: SearchableSelectOption) => ReactNode;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  listHeader,
  renderOption,
  disabled,
  clearable,
  className,
}: SearchableSelectProps) {
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  return (
    <div ref={portalContainerRef} className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !selectedOption && "text-muted-foreground",
              className,
            )}
          >
            <span className="truncate">
              {selectedOption?.label || placeholder}
            </span>
            {/*{clearable && selectedOption && !disabled ? (
              <X
                role="button"
                aria-label="Clear selection"
                className="opacity-50 transition-opacity hover:opacity-100"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onChange("");
                }}
              />
            ) : (*/}
              <ChevronsUpDown className="opacity-50" />
            {/*)}*/}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-fit min-w-[var(--radix-popover-trigger-width)] max-w-[56rem] p-0"
          align="start"
          portalContainer={portalContainerRef.current}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              {listHeader ? (
                <div className="sticky top-0 z-10 border-b bg-muted/95 px-2 py-1 text-xs text-muted-foreground backdrop-blur supports-[backdrop-filter]:bg-muted/80">
                  {listHeader}
                </div>
              ) : null}
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value} ${option.label}`}
                    keywords={option.keywords}
                    className="justify-between"
                    onSelect={() => {
                      if (clearable && option.value === value) {
                        onChange("");
                      } else {
                        onChange(option.value);
                      }
                      setOpen(false);
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      {renderOption ? (
                        renderOption(option)
                      ) : (
                        <span className="truncate">{option.label}</span>
                      )}
                    </div>
                    {value === option.value && (
                      <Check className="shrink-0 opacity-70" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
